<template>
  <div class="cm">
    <p v-if="!pages.length" class="cm-state">{{ $t('CM_NO_PAGES') }}</p>

    <p v-else-if="!filteredPages.length" class="cm-state">{{ $t('CM_NO_PAGES_FOR_FILTER') }}</p>

    <p v-else-if="scopeError" class="cm-state is-error">{{ scopeError }}</p>

    <!-- scope ещё не разрешён (первый рендер идёт до resolve) -->
    <p v-else-if="resolving || !scope" class="cm-state">{{ $t('CM_LOADING') }}</p>

    <!--
      Ни одна страница не заведена в контент-мониторинге. Пустой page_ids в CM
      означает «все страницы», поэтому запрос тут делать нельзя: вместо «данных
      нет» показали бы статистику по чужим сообществам.
    -->
    <div v-else-if="scope.unmatched" class="cm-state">
      <!-- страница только что добавлена и ещё собирается: прелоадер + авто-обновление -->
      <template v-if="collecting">
        <p class="cm-collecting"><span class="cm-spinner"></span>{{ $t('CM_COLLECTING') }}</p>
        <p class="cm-hint">{{ $t('CM_COLLECTING_HINT') }}</p>
      </template>
      <template v-else>
        <p>{{ $t('CM_PAGES_NOT_MONITORED') }}</p>
        <p class="cm-hint">{{ notMonitoredHint }}</p>
        <p class="cm-hint" v-if="scope.cmPages && scope.cmPages.length">
          {{ $t('CM_AVAILABLE_PAGES') }}: {{ availableTitles }}
        </p>
      </template>
    </div>

    <template v-else>
      <!--<p v-if="scope.notInCm" class="cm-hint cm-partial">
        {{ partialHint }}
      </p>-->

      <!-- какие именно страницы попали в раздел: иначе цифры не с чем сверить -->
      <!--<div class="cm-scope">
        <button class="cm-scope-toggle" @click="showScope = !showScope">
          {{ $t('CM_SCOPE_PAGES', { n: scopePages.length }) }}
          <span>{{ showScope ? '▲' : '▼' }}</span>
        </button>
        <ul v-if="showScope" class="cm-scope-list">
          <li v-for="p in scopePages" :key="p.page_id">
            <span :class="`soc-ico soc-${String(p.social || '').toLowerCase()}`"></span>
            <span class="cm-scope-name">{{ p.title }}</span>
            <span class="cm-mention-num">{{ p.posts_in_period }} {{ $t('CM_POSTS_SHORT') }}</span>
          </li>
        </ul>
      </div>-->

      <Cm-section-head
        section-id="cm-content-monitoring"
        :title="$t('CM_PAGE_TITLE')"
        :description="$t('CM_PAGE_DESC')"
        block="summary"
        :collapsible="overviewHasCards"
      />

      <!-- ни у одной секции нет данных под текущий фильтр -->
      <div v-if="allSectionsEmpty" class="cm-state">
        <p>{{ $t(emptyStateKey) }}</p>
        <p v-if="isMediaFormat" class="cm-hint">{{ $t('CM_NO_MEDIA_DATA_HINT') }}</p>
        <p v-else-if="!contentFiltersActive" class="cm-hint">{{ $t('CM_NOT_ENOUGH_POSTS_HINT') }}</p>
      </div>

      <!-- секции поднимаем по одной (setTimeout 0), чтобы не блокировать UI -->
      <Cm-overview v-if="sectionGate >= 1" :pages="filteredPages" :scope="scope" @state="onSectionState" />
      <Cm-section-head
        v-if="showDynamicsHead"
        numbered
        collapsible
        section-id="cm-dynamics"
        :number="dynamicsNumber"
        :title="$t('CM_SEC_DYNAMICS')"
        :description="$t('CM_SEC_DYNAMICS_DESC')"
        block="dynamics"
      />
      <Cm-section-body block="dynamics">
        <Cm-timelines
          v-if="sectionGate >= 2"
          :pages="filteredPages"
          :scope="scope"
          @state="onSectionState"
        />
        <Cm-mentions
          v-if="sectionGate >= 3"
          :pages="filteredPages"
          :scope="scope"
          @state="onSectionState"
        />
      </Cm-section-body>
      <Cm-section-head
        v-if="postsVisible"
        numbered
        collapsible
        section-id="cm-posts"
        :number="postsNumber"
        :title="$t('CM_SEC_POSTS')"
        :description="$t('CM_SEC_POSTS_DESC')"
        block="posts"
      />
      <Cm-section-body block="posts">
        <Cm-feed v-if="sectionGate >= 4" :pages="filteredPages" :scope="scope" @state="onSectionState" />
      </Cm-section-body>

      <Cm-drilldown
        v-if="drillTerm"
        :key="'cm-drill-' + drillFormat + drillEntity + drillTerm"
        :term="drillTerm"
        :format="drillFormat"
        :entity="drillEntity"
        :scope="scope"
        @close="drillTerm = ''"
      />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import bus from "@/models/content-monitoring/bus";
import cmApi from "@/api/controllers/content-monitoring";
import { CM_LOAD_WORDS } from "@/store/actions/contentMonitoring";
import { cmHeadVisibility, cmHeadNumber } from "@/models/content-monitoring/section-heads";
import { filterCmPages } from "@/models/content-monitoring/filter-pages";
import CmOverview from "./Cm-overview";
import CmTimelines from "./Cm-timelines";
import CmMentions from "./Cm-mentions";
import CmFeed from "./Cm-feed";
import CmDrilldown from "./Cm-drilldown";
import CmSectionHead from "./Cm-section-head";
import CmSectionBody from "./Cm-section-body";

export default {
  name: "Dash-content-monitoring",

  components: {
    CmOverview,
    CmTimelines,
    CmMentions,
    CmFeed,
    CmDrilldown,
    CmSectionHead,
    CmSectionBody,
  },

  props: {
    pages: { type: Array, default: () => [] },
  },

  data() {
    return {
      drillTerm: "",
      // источник слова drill-down: 'media' — чип с картинки (считаем по картинкам),
      // '' — как текущая вкладка
      drillFormat: "",
      // кликнули аккаунт/ссылку в «Упоминаниях»: drill-down ищет терм как упоминание
      drillEntity: false,
      scope: null,
      resolving: false,
      scopeError: "",
      showScope: false,
      // состояние каждой секции: loading | error | empty | data
      sectionStates: {},
      // выбранная страница ещё не заведена в CM, но идёт авто-сбор (MVP-5) →
      // показываем прелоадер «собираем…» вместо заглушки «не в мониторинге»
      collecting: false,
      // сколько секций уже смонтировано (1..4); 0 — ждём scope
      sectionGate: 0,
    };
  },

  computed: {
    ...mapGetters([
      "getCmOwner",
      "getCmCategory",
      "getCmSocials",
      "getCategories",
      "getCmFormat",
      "getCmExcluded",
    ]),

    /** Вкладка «Картинки»: пустой раздел объясняется иначе, чем у текста. */
    isMediaFormat() {
      return this.getCmFormat === "media";
    },

    /** Фильтры шапки, из-за которых выборка может оказаться пустой. */
    contentFiltersActive() {
      return !!(
        this.getCmOwner ||
        this.getCmCategory != null ||
        (this.getCmSocials && this.getCmSocials.length) ||
        (this.getCmExcluded && this.getCmExcluded.length)
      );
    },

    /**
     * Пустой текст без фильтров — это порог расчёта, а не «не тот фильтр»:
     * при малом числе постов метрики не считаются.
     */
    emptyStateKey() {
      if (this.isMediaFormat) return "CM_NO_MEDIA_DATA";
      if (this.contentFiltersActive) return "CM_NO_DATA";
      return "CM_NOT_ENOUGH_POSTS";
    },

    /** Секции, которые сами сообщают, есть ли у них данные. */
    sectionKeys() {
      return ["Cm-overview", "Cm-timelines", "Cm-mentions", "Cm-feed"];
    },

    /** Все секции отчитались empty — и ни одна ещё не в loading. */
    allSectionsEmpty() {
      if (this.sectionGate < this.sectionKeys.length) return false;
      return this.sectionKeys.every((key) => this.sectionStates[key] === "empty");
    },

    /**
     * Страницы после применения фильтров шапки (владелец/категория).
     * Пустой фильтр (null) — «все страницы», ограничения нет.
     */
    filteredPages() {
      return filterCmPages(this.pages, {
        owner: this.getCmOwner,
        category: this.getCmCategory,
        categories: this.getCategories,
        socials: this.getCmSocials,
      });
    },

    /** Страницы, реально попавшие в выборку (по ним и посчитаны все цифры). */
    scopePages() {
      const ids = new Set(this.scope.pageIds);
      return this.scope.cmPages
        .filter((p) => ids.has(Number(p.page_id)))
        .sort((a, b) => (b.posts_in_period || 0) - (a.posts_in_period || 0));
    },

    /** Ключ пересчёта: page_id — тот же ключ, что уходит в CM. */
    pagesKey() {
      return this.filteredPages.map((p) => p.page_id).join(",");
    },

    availableTitles() {
      return this.scope.cmPages.map((p) => p.title).join(", ");
    },

    notMonitoredHint() {
      return this.$t("CM_SKIPPED_NOT_IN_CM", { n: this.scope.notInCm });
    },

    /** Часть страниц посчиталась, часть — нет: честно говорим, что скоуп урезан. */
    partialHint() {
      return this.$t("CM_PARTIAL_SCOPE", {
        matched: this.scope.matched,
        total: this.scope.totalSelected,
      });
    },

    /** Секция с данными: смонтирована и не empty. */
    overviewHasCards() {
      return this.sectionStates["Cm-overview"] === "data";
    },

    timelinesVisible() {
      return this.sectionHasData("Cm-timelines", 2);
    },

    mentionsVisible() {
      return this.sectionHasData("Cm-mentions", 3);
    },

    /** Один номер на графики динамики и блок упоминаний. */
    showDynamicsHead() {
      return this.timelinesVisible || this.mentionsVisible;
    },

    postsVisible() {
      return this.sectionHasData("Cm-feed", 4);
    },

    dynamicsNumber() {
      return cmHeadNumber("dynamics");
    },

    postsNumber() {
      return cmHeadNumber("posts");
    },
  },

  watch: {
    pagesKey() {
      this.resolve();
    },
    scope(val) {
      if (val && !val.unmatched) this.openSectionGate();
      else this.resetSectionGate();
    },
    showDynamicsHead: {
      immediate: true,
      handler(visible) {
        cmHeadVisibility.dynamics = !!visible;
      },
    },
    postsVisible: {
      immediate: true,
      handler(visible) {
        cmHeadVisibility.posts = !!visible;
      },
    },
  },

  methods: {
    ...mapActions([CM_LOAD_WORDS]),

    openTerm(term, source, opts) {
      // источник чипа (text | media) важнее вкладки; из топов/таймлайнов
      // приходит без источника — тогда формат вкладки
      this.drillFormat = source === "media" || source === "text" ? source : "";
      this.drillEntity = !!(opts && opts.entity);
      this.drillTerm = term;
    },

    onSectionState(key, state) {
      this.$set(this.sectionStates, key, state);
      if (state === "loading") return;
      const currentKey = this.sectionKeys[this.sectionGate - 1];
      if (key === currentKey) this.scheduleAdvanceGate();
    },

    sectionHasData(key, gate) {
      if (this.sectionGate < gate) return false;
      const state = this.sectionStates[key];
      return !!state && state !== "empty";
    },

    resetSectionGate() {
      if (this._gateTimer) {
        clearTimeout(this._gateTimer);
        this._gateTimer = null;
      }
      if (this._gateRaf) {
        cancelAnimationFrame(this._gateRaf);
        this._gateRaf = null;
      }
      this.sectionGate = 0;
    },

    openSectionGate() {
      this.resetSectionGate();
      this.advanceGate();
    },

    scheduleAdvanceGate() {
      this.$nextTick(() => {
        this._gateRaf = requestAnimationFrame(() => {
          this._gateRaf = null;
          this.advanceGate();
        });
      });
    },

    advanceGate() {
      if (this.sectionGate < this.sectionKeys.length) this.sectionGate += 1;
    },

    resolve() {
      this.stopCollectPolling();
      this.collecting = false;
      this.resetSectionGate();

      if (!this.filteredPages.length) {
        this.scope = null;
        this.sectionStates = {};
        return;
      }

      // быстрая смена фильтра (владелец/категория) — не даём старому resolve
      // перезаписать свежий scope
      const id = (this.resolveId += 1);

      this.resolving = true;
      this.scopeError = "";
      // сбрасываем, иначе после remount секций мигает CM_NO_DATA по старым empty
      this.sectionStates = {};

      cmApi
        .resolveScope(this.filteredPages)
        .then((scope) => {
          if (id !== this.resolveId) return;
          this.scope = scope;
          this.evaluateCollecting();
        })
        .catch((e) => {
          if (id !== this.resolveId) return;
          this.scopeError = e.message;
        })
        .finally(() => {
          if (id === this.resolveId) this.resolving = false;
        });
    },

    /**
     * Ни одна выбранная страница не заведена в CM (unmatched). Спрашиваем сервер,
     * не идёт ли по ним авто-сбор (pending-pages): если да — показываем прелоадер
     * и начинаем опрашивать, пока страница не появится в CM.
     */
    evaluateCollecting() {
      this.stopCollectPolling();
      if (!this.scope || !this.scope.unmatched) {
        this.collecting = false;
        return;
      }
      const id = this.resolveId;
      cmApi.fetchPendingPages().then((pendingIds) => {
        if (id !== this.resolveId) return; // фильтр сменился
        if (!this.scope || !this.scope.unmatched) {
          this.collecting = false;
          return;
        }
        const pending = new Set(pendingIds);
        const selected = this.filteredPages.map((p) => Number(p.page_id));
        // все выбранные (и отсутствующие в CM) страницы ждут сбора?
        this.collecting = selected.length > 0 && selected.every((pid) => pending.has(pid));
        if (this.collecting) this.scheduleCollectPoll(id);
      });
    },

    /** Пока идёт сбор — тихо перезапрашиваем scope (свежий /pages), без мигания loading. */
    scheduleCollectPoll(id) {
      this.collectTimer = setTimeout(() => {
        if (id !== this.resolveId) return;
        this.collectPolls += 1;
        cmApi
          .resolveScope(this.filteredPages, { fresh: true })
          .then((scope) => {
            if (id !== this.resolveId) return;
            this.scope = scope;
            if (!scope.unmatched) {
              this.collecting = false; // страница появилась в CM → секции отрисуются
            } else if (this.collectPolls >= this.maxCollectPolls) {
              this.collecting = false; // достаточно ждали — показываем обычную заглушку
            } else {
              this.scheduleCollectPoll(id);
            }
          })
          .catch(() => {
            this.collecting = false;
          });
      }, this.collectPollMs);
    },

    stopCollectPolling() {
      if (this.collectTimer) {
        clearTimeout(this.collectTimer);
        this.collectTimer = null;
      }
      this.collectPolls = 0;
    },
  },

  created() {
    this.resolveId = 0;
    // авто-сбор (MVP-5): опрос, пока новая страница не появится в CM
    this.collectTimer = null;
    this.collectPolls = 0;
    this.collectPollMs = 20000; // раз в 20с (крон сбора ходит каждые 2 мин)
    this.maxCollectPolls = 45; // ~15 мин, потом показываем обычную заглушку
    bus.$on("cm:term", this.openTerm);
  },

  mounted() {
    this.CM_LOAD_WORDS();
    this.resolve();
  },

  beforeDestroy() {
    this.stopCollectPolling();
    this.resetSectionGate();
    cmHeadVisibility.dynamics = false;
    cmHeadVisibility.posts = false;
    bus.$off("cm:term", this.openTerm);
  },
};
</script>

<style scoped>
.cm-collecting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.cm-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  display: inline-block;
  animation: cm-spin 0.8s linear infinite;
}
@keyframes cm-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
